import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { Copy, Download, RefreshCw } from "lucide-react";
import api, { describeAxiosError } from "../../lib/api";
import { useSite } from "../../contexts/SiteContext";

const card = "rounded-2xl border border-white/10 bg-[#111D33] p-5";

export const RecruitCoaches = () => {
  const { L } = useSite();
  const [d, setD] = useState(null);
  const qr = useRef(null);
  const load = () => api.get("/admin/recruit-link").then(({ data }) => setD(data)).catch((e) => toast.error(describeAxiosError(e)));
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const url = `${window.location.origin}${d.share_path}`;
  const copy = async () => { try { await navigator.clipboard.writeText(url); toast.success(L("Link copied", "Линкът е копиран")); } catch (_) { toast.error("Copy failed"); } };
  const download = () => { const a = document.createElement("a"); a.href = qr.current.querySelector("canvas").toDataURL("image/png"); a.download = `onthepitch-coach-invite-${d.code}.png`; a.click(); };
  const rotate = async () => {
    if (!window.confirm(L("Create a new link? The old link and QR code will stop working.", "Нов линк? Старият линк и QR код спират да работят."))) return;
    try { const { data } = await api.post("/admin/recruit-link/rotate"); setD(data); toast.success(L("New link created", "Създаден е нов линк")); } catch (e) { toast.error(describeAxiosError(e)); }
  };
  const chip = (s) => (s === "approved" ? "text-pitch" : s === "rejected" ? "text-red-400" : "text-amber-300");
  return (
    <div className="space-y-4" data-testid="admin-recruit">
      <div className={`${card} grid md:grid-cols-[auto_1fr] gap-6 items-center`}>
        <div ref={qr} className="bg-white p-3 rounded-xl w-fit" data-testid="recruit-qr"><QRCodeCanvas value={url} size={180} level="M" /></div>
        <div className="space-y-3 min-w-0">
          <p className="font-semibold">{L("Personal coach invitation (Los Angeles)", "Лична покана за треньори (Лос Анджелис)")}</p>
          <p className="text-xs text-white/50">{L("Free registration. Coaches who join through this link still need your manual approval before they appear publicly.", "Безплатна регистрация. Треньорите през този линк пак чакат твоето ръчно одобрение.")}</p>
          <div className="flex gap-2 items-center"><code className="flex-1 min-w-0 truncate bg-[#0A1628] border border-white/10 rounded-lg px-3 py-2 text-xs" data-testid="recruit-url">{url}</code>
            <button type="button" onClick={copy} className="btn-primary px-3 py-2 text-xs inline-flex items-center gap-1" data-testid="recruit-copy-btn"><Copy className="w-3.5 h-3.5" />{L("Copy", "Копирай")}</button></div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={download} className="btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1" data-testid="recruit-download-qr"><Download className="w-3.5 h-3.5" />{L("Download QR", "Свали QR")}</button>
            <button type="button" onClick={rotate} className="btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1" data-testid="recruit-rotate-btn"><RefreshCw className="w-3.5 h-3.5" />{L("New link", "Нов линк")}</button>
          </div>
          <p className="text-xs text-white/60" data-testid="recruit-count">{L("Coaches joined via this link", "Треньори през този линк")}: <b>{d.used_count}</b></p>
        </div>
      </div>
      <div className={card} data-testid="recruit-signups">
        <p className="font-semibold mb-3">{L("Sign-ups", "Регистрации")}</p>
        {!d.signups.length ? <p className="text-sm text-white/50">{L("No coaches yet — share the link or QR code.", "Още няма — сподели линка или QR кода.")}</p> : d.signups.map((u) => (
          <div key={u.id} className="flex flex-wrap justify-between gap-2 py-2 border-b border-white/5 text-sm" data-testid={`recruit-signup-${u.id}`}>
            <span><b>{u.name}</b> <span className="text-white/40 text-xs">{u.email}</span></span>
            <span className={`text-xs font-mono uppercase ${chip(u.approval_status)}`}>{u.approval_status || "pending"}</span>
          </div>
        ))}
        <p className="text-[11px] text-white/40 mt-3">{L("Approve new coaches in Admin → Approvals.", "Одобрявай новите треньори в Админ → Одобрения.")}</p>
      </div>
    </div>
  );
};

const Row = ({ k, v, ok, testid }) => (
  <div className="flex justify-between gap-3 py-2 border-b border-white/5 text-sm" data-testid={testid}>
    <span className="text-white/60">{k}</span><span className={ok === undefined ? "" : ok ? "text-pitch" : "text-amber-300"}>{v}</span>
  </div>
);

export const LaunchStatus = () => {
  const { L } = useSite();
  const [s, setS] = useState(null);
  const [logRows, setLog] = useState([]);
  useEffect(() => {
    api.get("/admin/launch-status").then(({ data }) => setS(data)).catch(() => {});
    api.get("/admin/email-log").then(({ data }) => setLog(data)).catch(() => {});
  }, []);
  if (!s) return null;
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="admin-launch">
      <div className={card}>
        <p className="font-semibold mb-2">{L("Payments & webhooks", "Плащания и webhooks")}</p>
        <Row k={L("Stripe mode", "Stripe режим")} v={s.stripe_mode.toUpperCase()} ok={s.stripe_mode === "test"} testid="launch-stripe-mode" />
        <Row k={L("Webhook secret", "Webhook ключ")} v={s.webhook_secret_configured ? L("Configured", "Настроен") : L("Missing", "Липсва")} ok={s.webhook_secret_configured} testid="launch-webhook-secret" />
        <Row k={L("Webhook URL", "Webhook адрес")} v={`${s.public_url || ""}${s.webhook_path}`} testid="launch-webhook-url" />
        <Row k={L("Last webhook received", "Последен webhook")} v={s.last_webhook_event ? `${s.last_webhook_event.type} · ${new Date(s.last_webhook_event.received_at).toLocaleString()}` : "—"} testid="launch-last-webhook" />
        <Row k="Stripe Connect" v={s.connect_enabled ? "ON" : L("Off (manual payouts)", "Изкл. (ръчни изплащания)")} testid="launch-connect" />
        <p className="text-[11px] text-white/40 mt-3">{L("Events to subscribe:", "Събития:")} {s.webhook_events.join(", ")}</p>
      </div>
      <div className={card}>
        <p className="font-semibold mb-2">{L("Email notifications", "Имейл известия")}</p>
        <Row k={L("Delivery", "Изпращане")} v={s.email_delivery_enabled ? L("Enabled", "Включено") : L("Disabled — events are logged only", "Изключено — само се записват")} ok={s.email_delivery_enabled} testid="launch-email-status" />
        <Row k={L("Sender name", "Име на подателя")} v={s.email_from_name || "—"} testid="launch-email-from" />
        <div className="mt-3 max-h-72 overflow-y-auto" data-testid="email-log">
          {!logRows.length ? <p className="text-sm text-white/50">{L("No email events yet.", "Още няма имейл събития.")}</p> : logRows.map((r) => (
            <div key={r.id} className="flex justify-between gap-2 py-1.5 border-b border-white/5 text-xs" data-testid={`email-log-${r.id}`}>
              <span className="truncate"><b>{r.event}</b> · {r.to_masked}</span>
              <span className={r.status === "sent" ? "text-pitch" : r.status === "failed" ? "text-red-400" : "text-white/50"}>{r.status} · {new Date(r.created_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
